import React, {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {updateDummyUser} from '../../../actions/buildingsActions.js'
import PageHeader from '../../PageHeader'
import {PageContainer} from '../../../assets/styles/layout'
import {Grid} from '@mui/material'
import DiscardChangesDialog from '../../DiscardChangesDialog'
import {makeStyles} from '@mui/styles'
import RXRButton from '../../RXRButton'
import TextInput from '../../TextInput'
import SelectInput from '../../SelectInput'
import {spaceMedium, spaceLarge} from '../../../assets/styles/spacing'
import useFormChanged, {NonEmptyValidator} from '../../hooks/useFormChanged'
import {nearWhiteColor} from '../../../assets/styles/color'
import useResidentLookup from '../../hooks/useResidentLookup'
import {OCCUPANCY_STATUS_PAST} from '../../../constants/ModelConstants'
import MessageBox from '../../MessageBox'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: nearWhiteColor,
  },
  mainContainer: {
    padding: spaceLarge,
    maxWidth: 500,
  },
  buttonWithMarginLeft: {
    marginLeft: spaceMedium,
  },
}))

const ACTIVE_STATUS = 'active'
const INACTIVE_STATUS = 'inactive'

const EMPTY_FORM_STATE = {
  name: '',
  phone: '',
  status: ACTIVE_STATUS,
}

function residentToDummyForm(resident) {
  return {
    name: resident.displayName || '',
    phone: resident.phoneNumber || '',
    status: resident.statusOverride === OCCUPANCY_STATUS_PAST ? INACTIVE_STATUS : ACTIVE_STATUS,
  }
}

export default function DummyAccountPage() {
  const classes = useStyles()
  const [isSaving, setIsSaving] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [error, setError] = useState(null)
  const dispatch = useDispatch()
  const activeBuilding = useSelector(state => state.Buildings.buildingsLookup[state.Buildings.activeBuildingId])
  const {getResidentOrNull} = useResidentLookup()
  const dummyResident = getResidentOrNull(activeBuilding.dummyResidentId)

  const {form, setForm, formChanged, resetInitialForm, invalidItems, validateForm} = useFormChanged(
    EMPTY_FORM_STATE,
    useFormChanged.PropLevelValidation({
      name: NonEmptyValidator,
      phone: NonEmptyValidator,
    }),
  )
  // Update form when testResident changes
  useEffect(() => {
    if (dummyResident) {
      resetInitialForm(residentToDummyForm(dummyResident))
    } else {
      resetInitialForm(EMPTY_FORM_STATE)
    }
  }, [dummyResident])

  const handleSave = () => {
    if (!validateForm()) {
      return
    }
    setIsSaving(true)
    setError(null)
    updateDummyUser(dispatch, activeBuilding.id, form.phone, form.name, form.status === ACTIVE_STATUS)
      .then(u => {
        resetInitialForm(residentToDummyForm(u))
      })
      .catch(err => {
        setError(err.message)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleCancel = () => {
    setShowCancelDialog(true)
  }

  const handleConfirmCancel = () => {
    if (dummyResident) {
      resetInitialForm(residentToDummyForm(dummyResident))
    } else {
      resetInitialForm(EMPTY_FORM_STATE)
    }
    setShowCancelDialog(false)
  }

  const handleCloseCancelDialog = () => {
    setShowCancelDialog(false)
  }

  return (
    <div className={classes.root} style={PageContainer}>
      <PageHeader title={'Dummy Account'} />
      <div className={classes.mainContainer}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextInput
              label="Name in App"
              value={form.name}
              onChange={val => setForm({name: val})}
              isRequired={true}
              error={invalidItems.includes('name')}
              maxLength={120}
              placeholder="Enter name"
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              label="Phone number"
              value={form.phone}
              onChange={val => setForm({phone: val})}
              isRequired={true}
              error={invalidItems.includes('phone')}
              maxLength={20}
              placeholder="Enter phone number"
            />
          </Grid>
          <Grid item xs={12}>
            <SelectInput
              label="Dummy account status"
              onChange={val => setForm({status: val})}
              value={form.status}
              isRequired={true}
              error={invalidItems.includes('status')}
              options={[
                {value: ACTIVE_STATUS, label: 'Active'},
                {value: INACTIVE_STATUS, label: 'Inactive'},
              ]}
            />
          </Grid>
          {!!error && (
            <Grid item xs={12} style={{marginTop: spaceMedium}}>
              <MessageBox>{error}</MessageBox>
            </Grid>
          )}
          <Grid item xs={12} style={{marginTop: spaceMedium}}>
            <RXRButton onClick={handleSave} isLoading={isSaving} disabled={!formChanged}>
              Save
            </RXRButton>
            <RXRButton
              type={RXRButton.TYPE_TEXT}
              className={classes.buttonWithMarginLeft}
              onClick={() => handleCancel()}
              disabled={!formChanged}
            >
              Cancel
            </RXRButton>
          </Grid>
        </Grid>
        <DiscardChangesDialog
          isOpen={showCancelDialog}
          discardTitle={'Cancel changes'}
          discardMessage={'Are you sure you want to cancel? Any unsaved changes will be lost.'}
          discardButton={'Yes, Cancel'}
          onDiscard={handleConfirmCancel}
          onCancel={handleCloseCancelDialog}
        />
      </div>
    </div>
  )
}
